import React, { useCallback, useEffect, useState } from 'react';
import { DocumentRequestAPI, TransactionDocumentsAPI } from '../services/api';
import { DocumentDialog } from './DocumentDialog';

// Details-panel section for the customer document-upload feature. Any role can
// send / resend / cancel the request link; shows the active link, its send
// history, and the documents on file (inline, plus the full Open-documents modal
// which keeps the existing listing + staff upload unchanged).
interface Props {
  recordId: string;
  recordTitle?: string;
}

type Method = 'email' | 'sms' | 'both';

const fmt = (iso?: string | null): string => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const typeLabel = (t?: string | null): string => {
  const m: Record<string, string> = {
    ssn_front: 'SSN Front', ssn_back: 'SSN Back',
    dl_front: 'DL Front', dl_back: 'DL Back', lease: 'Lease',
  };
  return t ? (m[t] || t) : '';
};

export const CustomerDocumentsSection: React.FC<Props> = ({ recordId, recordTitle }) => {
  const [data, setData] = useState<any>(null);   // { active, history }
  const [docs, setDocs] = useState<any[]>([]);
  const [method, setMethod] = useState<Method>('email');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showDocs, setShowDocs] = useState(false);
  const [copied, setCopied] = useState(false);
  const [provider, setProvider] = useState('');

  const load = useCallback(async () => {
    if (!recordId) return;
    try { setData(await DocumentRequestAPI.get(recordId)); } catch { setData({ active: null, history: [] }); }
    try { const r = await TransactionDocumentsAPI.getDocuments(recordId); setDocs(r.documents || []); } catch { setDocs([]); }
  }, [recordId]);

  useEffect(() => { load(); }, [load]);

  const active = data?.active || null;
  const history: any[] = data?.history || [];
  const providers: string[] = data?.providers || [];
  const effProvider = providers.length === 1 ? providers[0] : (providers.includes(provider) ? provider : '');

  const run = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const res = await fn();
      setData({ active: res.active ?? null, history: res.history ?? history });
      setMsg(ok);
      setTimeout(() => setMsg(null), 3000);
    } catch (e: any) {
      setErr(e.message || 'Something went wrong');
    } finally { setBusy(false); }
  };

  const copy = () => {
    if (!active?.url) return;
    navigator.clipboard?.writeText(active.url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  };

  // When a link is active, only offer methods that have a contact on file.
  const methodOptions: Method[] = active
    ? ([['email', active.has_email], ['sms', active.has_phone], ['both', active.has_email && active.has_phone]]
        .filter(([, ok]) => ok).map(([m]) => m) as Method[])
    : ['email', 'sms', 'both'];
  const effMethod: Method = methodOptions.includes(method) ? method : (methodOptions[0] || 'email');

  return (
    <div className="cds">
      <div className="cds-head">
        <span className="cds-title">Customer documents</span>
        <button type="button" className="cds-btn-sec" onClick={() => setShowDocs(true)}>Open documents</button>
      </div>

      {/* Active link / send */}
      {active ? (
        <div className="cds-active">
          <div className="cds-row">
            <span className={`cds-chip cds-chip-${(active.status || '').toLowerCase()}`}>{(active.status || '').toLowerCase()}</span>
            <span className="cds-meta">Sent by {active.created_by_name || 'unknown'} · {fmt(active.created_at)}</span>
          </div>
          <div className="cds-meta">Expires {fmt(active.expiry_at)}{active.opened_at ? ` · opened ${fmt(active.opened_at)}` : ''}</div>
          <div className="cds-linkrow">
            <input readOnly className="cds-linkinput" value={active.url || ''} onFocus={(e) => e.target.select()} />
            <button type="button" className="cds-btn-sec" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
          </div>
          <div className="cds-actions">
            <select value={effMethod} onChange={(e) => setMethod(e.target.value as Method)} disabled={busy}>
              {methodOptions.map((m) => <option key={m} value={m}>{m === 'both' ? 'Email + SMS' : m.toUpperCase()}</option>)}
            </select>
            <button type="button" className="cds-btn" disabled={busy} onClick={() => run(() => DocumentRequestAPI.resend(recordId, effMethod), 'Link resent.')}>Resend</button>
            <button type="button" className="cds-btn-danger" disabled={busy} onClick={() => run(() => DocumentRequestAPI.cancel(recordId), 'Link cancelled.')}>Cancel link</button>
          </div>
        </div>
      ) : (
        <div className="cds-active">
          <div className="cds-meta">No active document link. Send one to request documents from the customer.</div>
          {providers.length === 0 ? (
            <div className="cds-err">This sale has no utility provider set. Add a provider on the sale before sending a document link.</div>
          ) : (
            <div className="cds-actions">
              {providers.length > 1 && (
                <select value={effProvider} onChange={(e) => setProvider(e.target.value)} disabled={busy}>
                  <option value="">Choose provider…</option>
                  {providers.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
              <select value={effMethod} onChange={(e) => setMethod(e.target.value as Method)} disabled={busy}>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="both">Email + SMS</option>
              </select>
              <button type="button" className="cds-btn" disabled={busy || (providers.length > 1 && !effProvider)}
                onClick={() => run(() => DocumentRequestAPI.create(recordId, effMethod, effProvider || undefined), 'Link sent.')}>
                Send document link
              </button>
            </div>
          )}
        </div>
      )}
      {msg && <div className="cds-ok">{msg}</div>}
      {err && <div className="cds-err">{err}</div>}

      {/* Send history */}
      {history.length > 0 && (
        <div className="cds-block">
          <div className="cds-subtitle">Link history</div>
          {history.map((h, i) => (
            <div key={i} className="cds-hist">
              <span className="cds-hist-ev">{h.event === 'resent' ? 'Resent' : 'Sent'}</span>
              <span className="cds-meta">by {h.sent_by_name || 'unknown'} · {h.method ? (h.method === 'both' ? 'Email + SMS' : h.method.toUpperCase()) : ''} · {fmt(h.sent_at)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Documents on file (inline) */}
      <div className="cds-block">
        <div className="cds-subtitle">Documents on file ({docs.length})</div>
        {docs.length === 0 ? (
          <div className="cds-meta">No documents yet.</div>
        ) : (
          <div className="cds-docs">
            {docs.map((d) => (
              <div key={d.id} className="cds-doc">
                <span className="cds-doc-name" title={d.original_filename}>{d.original_filename}</span>
                {d.doc_type && <span className="cds-badge">{typeLabel(d.doc_type)}{d.version ? ` v${d.version}` : ''}</span>}
                <span className="cds-meta cds-doc-meta">{d.uploader_name || ''} · {fmt(d.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDocs && (
        <DocumentDialog recordId={recordId} isOpen={true} onClose={() => { setShowDocs(false); load(); }} recordTitle={recordTitle} />
      )}

      <style>{`
        .cds { display: flex; flex-direction: column; gap: 10px; }
        .cds-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
        .cds-title { font-size: 0.9rem; font-weight: 600; color: var(--text-primary); }
        .cds-subtitle { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: var(--text-secondary); margin-bottom: 6px; }
        .cds-active { background: var(--bg-secondary, var(--bg-card)); border: 1px solid var(--border-primary); border-radius: 8px; padding: 10px 12px; display: flex; flex-direction: column; gap: 8px; }
        .cds-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .cds-meta { font-size: 0.78rem; color: var(--text-secondary); }
        .cds-chip { font-size: 0.7rem; font-weight: 700; padding: 2px 9px; border-radius: 999px; text-transform: capitalize; background: var(--bg-tertiary, var(--bg-card)); color: var(--text-secondary); border: 1px solid var(--border-primary); }
        .cds-chip-sent { background: #eef2ff; color: #4f46e5; border-color: #c7d2fe; }
        .cds-chip-opened { background: #ecfeff; color: #0e7490; border-color: #a5f3fc; }
        .cds-linkrow { display: flex; gap: 8px; }
        .cds-linkinput { flex: 1; min-width: 0; height: 30px; padding: 4px 8px; border: 1px solid var(--input-border, var(--border-primary)); border-radius: 6px; background: var(--input-bg, var(--bg-card)); color: var(--text-primary); font-size: 0.75rem; }
        .cds-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
        .cds-actions select { height: 32px; padding: 5px 8px; border: 1px solid var(--input-border, var(--border-primary)); border-radius: 6px; background: var(--input-bg, var(--bg-card)); color: var(--text-primary); font-size: 0.8125rem; }
        .cds-btn { background: var(--primary); color: var(--text-inverse, #fff); border: none; padding: 8px 14px; border-radius: 7px; font-size: 0.8125rem; font-weight: 600; cursor: pointer; }
        .cds-btn:disabled { opacity: .6; cursor: default; }
        .cds-btn-sec { background: var(--bg-tertiary, var(--bg-secondary)); color: var(--text-primary); border: 1px solid var(--border-primary); padding: 7px 12px; border-radius: 7px; font-size: 0.8rem; font-weight: 600; cursor: pointer; }
        .cds-btn-danger { background: none; color: var(--danger); border: 1px solid var(--danger); padding: 8px 12px; border-radius: 7px; font-size: 0.8125rem; font-weight: 600; cursor: pointer; }
        .cds-btn-danger:disabled { opacity: .6; cursor: default; }
        .cds-ok { color: var(--success); font-size: 0.8rem; font-weight: 600; }
        .cds-err { color: var(--danger); font-size: 0.8rem; }
        .cds-block { border-top: 1px dashed var(--border-primary); padding-top: 10px; }
        .cds-hist { display: flex; gap: 8px; align-items: baseline; padding: 2px 0; }
        .cds-hist-ev { font-size: 0.78rem; font-weight: 600; color: var(--text-primary); }
        .cds-docs { display: flex; flex-direction: column; gap: 6px; }
        .cds-doc { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .cds-doc-name { font-size: 0.8125rem; color: var(--text-primary); max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .cds-badge { font-size: 0.68rem; font-weight: 700; padding: 1px 7px; border-radius: 999px; background: var(--primary-light); color: var(--primary); }
        .cds-doc-meta { margin-left: auto; }
      `}</style>
    </div>
  );
};

export default CustomerDocumentsSection;
